import { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { Mail, CheckCircle2, XCircle, Loader2, Send, ShoppingCart, UserPlus, RotateCcw, Megaphone, PackageCheck, FlaskConical } from 'lucide-react';

interface EmailSettings {
  email_notify_order_status: boolean;
  email_notify_registration: boolean;
  email_notify_rma: boolean;
  email_notify_stock_alerts: boolean;
  email_notify_admin_alerts: boolean;
  email_admin_recipients: string;
  dev_email_enabled: boolean;
  dev_email_address: string;
  contact_form_recipient: string;
  sendgrid_configured: boolean;
  sender_email: string;
  sender_name: string;
  app_base_url: string;
}

function SectionCard({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center gap-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <h2 className="font-semibold">{title}</h2>
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </div>
  );
}

function ToggleRow({ icon: Icon, label, hint, checked, onChange, testId }: {
  icon: React.ElementType; label: string; hint: string; checked: boolean; onChange: (v: boolean) => void; testId: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-1">
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testId}
        onClick={() => onChange(!checked)}
        className={`flex-shrink-0 w-10 h-6 rounded-full transition-colors relative ${checked ? 'bg-[hsl(214,58%,41%)]' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
      </button>
    </div>
  );
}

export default function AdminEmailSettingsPage() {
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [recipients, setRecipients] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<EmailSettings>('/api/admin/email-settings');
      setSettings(data);
      setRecipients(data.email_admin_recipients);
    } catch {
      toast.error('Errore nel caricamento delle impostazioni email');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (updates: Partial<EmailSettings>) => {
    if (!settings) return;
    setSettings({ ...settings, ...updates });
  };

  const save = async () => {
    if (!settings) return;
    if (settings.dev_email_enabled && !settings.dev_email_address.trim()) {
      toast.error('Inserisci un indirizzo email di sviluppo prima di attivare la modalità.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/api/admin/email-settings', {
        email_notify_order_status: settings.email_notify_order_status,
        email_notify_registration: settings.email_notify_registration,
        email_notify_rma: settings.email_notify_rma,
        email_notify_stock_alerts: settings.email_notify_stock_alerts,
        email_notify_admin_alerts: settings.email_notify_admin_alerts,
        email_admin_recipients: recipients,
        dev_email_enabled: settings.dev_email_enabled,
        dev_email_address: settings.dev_email_address,
        contact_form_recipient: settings.contact_form_recipient,
      });
      toast.success('Impostazioni email salvate');
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error('Inserisci un indirizzo email'); return; }
    setSendingTest(true);
    try {
      const { data } = await api.post<{ ok: boolean; message: string }>('/api/admin/email-settings/test', { to_email: testEmail.trim() });
      if (data.ok) toast.success(data.message); else toast.error(data.message);
    } catch {
      toast.error('Invio email di test non riuscito');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6" data-testid="admin-email-settings-page">
      <div className="flex items-center gap-3">
        <Mail className="w-5 h-5 text-[hsl(214,58%,41%)]" />
        <h1 className="text-xl font-semibold">Email & Notifiche</h1>
      </div>

      {/* Stato configurazione SendGrid (solo lettura, credenziali in .env) */}
      <SectionCard icon={Send} title="Stato Configurazione SendGrid">
        <div className="flex items-center gap-2" data-testid="sendgrid-status-badge">
          {settings.sendgrid_configured ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">SendGrid configurato e attivo</span>
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">SendGrid non configurato — le email non vengono inviate</span>
            </>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
          <div><span className="font-medium text-foreground">Mittente:</span> {settings.sender_name}</div>
          <div><span className="font-medium text-foreground">Indirizzo:</span> {settings.sender_email}</div>
          <div><span className="font-medium text-foreground">URL app:</span> {settings.app_base_url || '—'}</div>
        </div>
        <p className="text-[11px] text-muted-foreground bg-[hsl(var(--dtk-canvas))] rounded-lg p-3">
          Per motivi di sicurezza, la chiave API SendGrid (<code>SENDGRID_API_KEY</code>) e il mittente
          (<code>EMAIL_FROM</code>, <code>EMAIL_FROM_NAME</code>) si configurano SOLO tramite variabili
          d'ambiente nel file <code>.env</code> del backend — mai da questa interfaccia. Le credenziali
          non devono mai essere salvate nel database o esposte via API.
        </p>
      </SectionCard>

      {/* Modalità Sviluppo (richiesta utente 24/02/2026) */}
      <SectionCard icon={FlaskConical} title="Modalità Sviluppo">
        <ToggleRow
          icon={FlaskConical}
          label="Mail di sviluppo"
          hint="Se attiva, TUTTE le email della piattaforma vengono inviate a questo indirizzo invece che ai destinatari reali — utile mentre la piattaforma è in sviluppo/test, per evitare che i clienti reali ricevano comunicazioni."
          checked={settings.dev_email_enabled}
          onChange={v => patch({ dev_email_enabled: v })}
          testId="toggle-dev-email"
        />
        {settings.dev_email_enabled && (
          <div>
            <div className="text-sm font-medium mb-1">Indirizzo email di sviluppo</div>
            <input
              type="email"
              value={settings.dev_email_address}
              onChange={e => patch({ dev_email_address: e.target.value })}
              placeholder="dev@dtksrl.eu"
              className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
              data-testid="dev-email-address-input"
            />
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2 flex items-start gap-2" data-testid="dev-email-warning">
              <FlaskConical className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>
                Con questa modalità attiva, <strong>nessuna email reale verrà inviata ai clienti</strong> — arriveranno tutte a questo indirizzo, con indicati in testa il destinatario previsto e il mittente originali. Ricordati di disattivarla prima di andare in produzione.
              </span>
            </div>
          </div>
        )}
      </SectionCard>

      {/* Notifiche cliente */}
      <SectionCard icon={UserPlus} title="Notifiche Cliente">
        <ToggleRow
          icon={ShoppingCart} label="Cambio di stato ordine" hint="Email al cliente quando lo stato di un ordine viene aggiornato (confermato, spedito, consegnato...)"
          checked={settings.email_notify_order_status} onChange={v => patch({ email_notify_order_status: v })} testId="toggle-order-status"
        />
        <ToggleRow
          icon={UserPlus} label="Iscrizione e attivazione account" hint="Email di benvenuto alla registrazione ed email di conferma quando l'admin attiva l'account"
          checked={settings.email_notify_registration} onChange={v => patch({ email_notify_registration: v })} testId="toggle-registration"
        />
        <ToggleRow
          icon={RotateCcw} label="Sistema resi (RMA)" hint="Email al cliente alla creazione della richiesta e ad ogni cambio di stato della pratica RMA"
          checked={settings.email_notify_rma} onChange={v => patch({ email_notify_rma: v })} testId="toggle-rma"
        />
        <ToggleRow
          icon={PackageCheck} label="Rientro scorta (Avvisi Stock)" hint="Email al cliente quando un articolo che stava aspettando in 'Avvisi Stock' torna disponibile"
          checked={settings.email_notify_stock_alerts} onChange={v => patch({ email_notify_stock_alerts: v })} testId="toggle-stock-alerts"
        />
      </SectionCard>

      {/* Notifiche amministrazione */}
      <SectionCard icon={Megaphone} title="Notifiche Amministrazione">
        <ToggleRow
          icon={Megaphone} label="Avvisi al team DTK" hint="Email interna per nuovo ordine, nuova richiesta RMA e nuova registrazione da approvare"
          checked={settings.email_notify_admin_alerts} onChange={v => patch({ email_notify_admin_alerts: v })} testId="toggle-admin-alerts"
        />
        <div>
          <div className="text-sm font-medium mb-1">Destinatari</div>
          <input
            type="text"
            value={recipients}
            onChange={e => setRecipients(e.target.value)}
            placeholder="admin@dtksrl.eu, ordini@dtksrl.eu"
            className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
            data-testid="admin-recipients-input"
          />
          <div className="text-[11px] text-muted-foreground mt-1">Uno o più indirizzi email separati da virgola.</div>
        </div>
      </SectionCard>

      {/* Form Contattaci (pagina pubblica /contatti) */}
      <SectionCard icon={Mail} title="Form Contattaci (Sito Pubblico)">
        <div>
          <div className="text-sm font-medium mb-1">Destinatario messaggi</div>
          <input
            type="email"
            value={settings.contact_form_recipient}
            onChange={e => patch({ contact_form_recipient: e.target.value })}
            placeholder="info@dtksrl.eu"
            className="w-full h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
            data-testid="contact-form-recipient-input"
          />
          <div className="text-[11px] text-muted-foreground mt-1">
            I messaggi inviati dal form "Contattaci" della pagina pubblica <code>/contatti</code> arrivano a questo indirizzo. Se lasciato vuoto, i messaggi vengono registrati solo come notifica interna admin (nessuna email inviata).
          </div>
        </div>
      </SectionCard>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="save-email-settings-btn"
          className="h-10 px-6 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(214,58%,35%)] transition-colors disabled:opacity-60 flex items-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Salva Impostazioni
        </button>
      </div>

      {/* Invia email di test */}
      <SectionCard icon={Send} title="Invia Email di Test">
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="tuoindirizzo@esempio.it"
            className="flex-1 h-9 px-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
            data-testid="test-email-input"
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={sendingTest}
            data-testid="send-test-email-btn"
            className="h-9 px-4 rounded-lg border text-sm font-medium hover:bg-[hsl(var(--dtk-canvas))] transition-colors disabled:opacity-60 flex items-center gap-2 justify-center"
          >
            {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Invia Test
          </button>
        </div>
      </SectionCard>
    </div>
  );
}
